/**
 * Modern Location Manager - Pure localStorage + GraphQL Storefront API
 * Handles location-based product data with no legacy dependencies
 */

// DEV MODE FLAG - Disabled for production performance
const DEV_MODE = false;

// A stored number alone may be an old automatic browsing value. Selection
// requires an explicit choice or a live reservation, irrespective of the number.
function getExplicitPickingStoreFromStorage() {
  let chosen = false;
  let storedLocation = null;
  try {
    chosen = localStorage.getItem('hf_picking_store_chosen') === '1';
    storedLocation = localStorage.getItem('selectedLocation') || localStorage.getItem('currentPickingStore');
  } catch (e) {
    // A live reservation cookie can still establish an explicit selection.
  }
  let cookieLocation = null;
  let reservationIsLive = false;
  try {
    const locationMatch = document.cookie.match(/(?:^|;\s*)picking_store=([^;]+)/);
    cookieLocation = locationMatch ? decodeURIComponent(locationMatch[1]) : null;
    const expiryMatch = document.cookie.match(/(?:^|;\s*)delivery_expires_time=([^;]+)/);
    const expiry = expiryMatch ? Number(decodeURIComponent(expiryMatch[1])) : 0;
    reservationIsLive = Number.isFinite(expiry) && expiry > Date.now();
  } catch (e) {
    // Malformed cookies never establish a store selection.
  }
  if (!chosen && !reservationIsLive) return null;
  const location = String(storedLocation || cookieLocation || '').trim();
  return /^\d+$/.test(location) ? location : null;
}

window.getExplicitPickingStore = getExplicitPickingStoreFromStorage;

class LocationManager {
  constructor() {
    this.currentLocation = this.getCurrentLocation();
    this.storefrontToken = this.getStorefrontToken();
    this.shopDomain = this.getShopDomain();
    this.apiVersion = '2025-10';
    this.pageSize = 25; // Optimized for performance - competitors use 36, we start with 25
    
    // Add caching
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    
    // Batch processing - optimized for 25 products per page
    this.batchQueue = [];
    this.batchTimeout = null;
    this.batchDelay = 100; // 100ms debounce
    
    // Shopify Location mapping: store number -> { gid, name }.
    // Sourced from Admin API locations query (harris-farm-direct), Jul 3 2026.
    // IMPORTANT: an unknown store number must NEVER be rewritten to another store
    // (see setLocation/persistBrowsingLocation) - a missing entry here previously
    // caused Maroubra (87) reservations to be silently re-pointed at Lane Cove (76).
    this.locations = {
      10: { id: 'gid://shopify/Location/71928676411', name: 'Pennant Hills' },
      11: { id: 'gid://shopify/Location/11', name: 'Meats Pennant Hills' }, // internal - no Shopify location
      24: { id: 'gid://shopify/Location/71928873019', name: 'St Ives' },
      28: { id: 'gid://shopify/Location/71928610875', name: 'Mosman' },
      32: { id: 'gid://shopify/Location/71928938555', name: 'Willoughby' },
      37: { id: 'gid://shopify/Location/71927922747', name: 'Broadway' },
      40: { id: 'gid://shopify/Location/71928184891', name: 'Erina' },
      44: { id: 'gid://shopify/Location/71928643643', name: 'Orange' },
      48: { id: 'gid://shopify/Location/71928479803', name: 'Manly' },
      49: { id: 'gid://shopify/Location/71928545339', name: 'Mona Vale' },
      51: { id: 'gid://shopify/Location/71927889979', name: 'Bowral' },
      52: { id: 'gid://shopify/Location/71927955515', name: 'Cammeray' },
      54: { id: 'gid://shopify/Location/71928709179', name: 'Potts Point' },
      56: { id: 'gid://shopify/Location/71927857211', name: 'Boronia Park' },
      57: { id: 'gid://shopify/Location/71927627835', name: 'Bondi Beach' },
      58: { id: 'gid://shopify/Location/71928086587', name: 'Drummoyne' },
      59: { id: 'gid://shopify/Location/71927562299', name: 'Bathurst' },
      63: { id: 'gid://shopify/Location/71928741947', name: 'Randwick' },
      64: { id: 'gid://shopify/Location/71928283195', name: 'Leichhardt' },
      65: { id: 'gid://shopify/Location/71927693371', name: 'Bondi Westfield' },
      66: { id: 'gid://shopify/Location/71928053819', name: 'Cooks Hill' },
      67: { id: 'gid://shopify/Location/71928315963', name: 'Lindfield' },
      68: { id: 'gid://shopify/Location/71927496763', name: 'Albury' },
      69: { id: 'gid://shopify/Location/71928840251', name: 'Rose Bay' },
      70: { id: 'gid://shopify/Location/71928905787', name: 'West End' },
      73: { id: 'gid://shopify/Location/73', name: 'Online' }, // internal - no Shopify location
      74: { id: 'gid://shopify/Location/71928217659', name: 'Isle of Capri' },
      75: { id: 'gid://shopify/Location/71927988283', name: 'Clayfield' },
      76: { id: 'gid://shopify/Location/71928250427', name: 'Lane Cove' },
      77: { id: 'gid://shopify/Location/71928152123', name: 'Dural' },
      80: { id: 'gid://shopify/Location/71928381499', name: 'Majura Park' },
      84: { id: 'gid://shopify/Location/71928807483', name: 'Redfern' },
      85: { id: 'gid://shopify/Location/71928512571', name: 'Marrickville' },
      86: { id: 'gid://shopify/Location/71971864635', name: 'Miranda' },
      87: { id: 'gid://shopify/Location/71971897403', name: 'Maroubra' }
    };

    // Log initialization info
    console.log('LocationManager initialized');
    console.log('Detected location:', this.currentLocation, `(${this.locations[this.currentLocation]?.name || 'Unknown'})`);
    console.log('Storage state:');
    console.log('  - selectedLocation:', localStorage.getItem('selectedLocation'));
    console.log('  - currentPickingStore:', localStorage.getItem('currentPickingStore'));
    console.log('  - picking_store cookie:', document.cookie.match(/picking_store=([^;]+)/)?.[1] || 'not found');

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.dispatchReadyEvent();
  }

  /**
   * Get storefront access token from theme settings or meta tag
   */
  getStorefrontToken() {
    // Try to get from meta tag first
    const metaToken = document.querySelector('meta[name="shopify-storefront-token"]');
    if (metaToken) return metaToken.content;
    
    // Fallback to window object
    return window.Shopify?.shop?.storefrontAccessToken || '';
  }

  /**
   * Get shop domain
   */
  getShopDomain() {
    return window.Shopify?.shop?.domain || window.location.hostname;
  }

  /** Return the explicit store, or null while browsing without a selection. */
  getCurrentLocation() {
    return getExplicitPickingStoreFromStorage();
  }

  /**
   * Get current Shopify Location ID
   */
  getCurrentLocationId() {
    const location = this.locations[this.getCurrentLocation()];
    return location ? location.id : null;
  }

  /**
   * Get location metafield key for current location
   */
  getLocationKey() {
    const location = this.getCurrentLocation();
    return location ? `location_${location}` : null;
  }

  /**
   * Set new location (with legacy system compatibility)
   */
  /**
   * Single atomic writer for location state. Keeps localStorage (source of truth)
   * and the picking_store cookie (the only signal the server sees) in lockstep so
   * the two can never drift. Pass { activeDelivery: true } only for a real reservation;
   * browsing-only persistence (e.g. "Decide Later") must NOT set it.
   */
  _writeLocationStorage(locationStr, options = {}) {
    localStorage.setItem('selectedLocation', locationStr);
    localStorage.setItem('currentPickingStore', locationStr); // Legacy system compatibility
    if (options.activeDelivery === true) {
      localStorage.setItem('activeDelivery', 'true');
    }
    // Cookie mirror (1 year) - required so server-side Liquid renders the same store.
    document.cookie = `picking_store=${locationStr}; path=/; max-age=${365 * 24 * 60 * 60}`;
    this.currentLocation = locationStr;

    // Only a real StorePicker confirmation or reservation writes this proof.
    // Merely reading existing storage must never create an explicit choice.
    try {
      localStorage.setItem('hf_picking_store_chosen', '1');
    } catch (e) {
      console.warn('LocationManager: could not persist hf_picking_store_chosen flag:', e);
    }
  }

  setLocation(locationId) {
    const locationStr = String(locationId ?? '').trim();
    if (!/^\d+$/.test(locationStr)) {
      console.warn('setLocation: a store number is required; selection unchanged');
      return this;
    }
    
    console.log('Setting location to:', locationStr);
    
    // NEVER rewrite an unknown store number to another store. The store number
    // comes from the delivery API and is what gets stamped on the order; silently
    // "defaulting" here caused reservations for stores missing from this.locations
    // (e.g. Maroubra 87) to be re-pointed at Lane Cove 76. Keep the number as-is;
    // the map is only used for display names / Shopify GIDs.
    if (!this.locations[locationStr]) {
      console.error(`⚠️ LocationManager: store ${locationStr} is MISSING from the locations map - keeping it anyway. Add it to this.locations in location-manager.js.`);
    }
    
    const oldLocation = this.currentLocation;
    
    // Reservation context: write all stores in lockstep AND flag active delivery.
    this._writeLocationStorage(locationStr, { activeDelivery: true });
    
    console.log('Location changed from', oldLocation, 'to', locationStr);
    console.log('Location name:', this.locations[locationStr]?.name);
    console.log('Updated cookie + localStorage picking store:', locationStr);
    
    // Clear cache when location changes
    console.log('Clearing cache for location change');
    this.clearCache();
    
    this.dispatchLocationChangeEvent();
    
    return this;
  }

  /**
   * Persist a browsing store WITHOUT reserving a delivery slot (e.g. "Decide Later").
   * Writes localStorage + cookie in lockstep but does not set activeDelivery and does
   * not dispatch a location-change reload (the caller controls navigation).
   */
  persistBrowsingLocation(locationId) {
    const locationStr = String(locationId ?? '').trim();
    if (!/^\d+$/.test(locationStr)) {
      console.warn('persistBrowsingLocation: a store number is required; selection unchanged');
      return this;
    }
    if (!this.locations[locationStr]) {
      console.error(`⚠️ LocationManager: store ${locationStr} is MISSING from the locations map - keeping it anyway. Add it to this.locations in location-manager.js.`);
    }
    this._writeLocationStorage(locationStr, { activeDelivery: false });
    this.clearCache();
    console.log('Persisted browsing store (no reservation):', locationStr);
    return this;
  }

  /** A real store value and explicit-choice/reservation proof are both required. */
  hasExplicitPickingStore() {
    return this.getCurrentLocation() !== null;
  }

  /** Refresh cached event state after a choice changes or a reservation expires. */
  refreshCurrentLocation() {
    const location = this.getCurrentLocation();
    if (location === this.currentLocation) return;
    this.previousLocation = this.currentLocation;
    this.currentLocation = location;
    this.clearCache();
    this.dispatchLocationChangeEvent();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    document.addEventListener('locationChanged', () => {
      this.currentLocation = this.getCurrentLocation();
      this.refreshProductDisplays();
    });
    document.addEventListener('deliveryReservationExpired', () => this.refreshCurrentLocation());

    // Listen for changes from the old delivery-drawer system
    this.setupLegacyLocationListeners();
  }

  /**
   * Setup listeners for legacy location change systems
   */
  setupLegacyLocationListeners() {
    window.addEventListener('storage', (event) => {
      if (event.key === 'currentPickingStore' || event.key === 'selectedLocation') {
        if (event.newValue && this.hasExplicitPickingStore()) this.syncFromLegacyLocation(event.newValue);
        else this.refreshCurrentLocation();
      } else if (event.key === 'hf_picking_store_chosen' || event.key === null) {
        this.refreshCurrentLocation();
      }
    });

    // Cookie polling also detects expiry of reservation-only selection proof.
    let lastCookieLocation = this.getCookieLocation();
    setInterval(() => {
      const cookieLocation = this.getCookieLocation();
      if (cookieLocation && cookieLocation !== lastCookieLocation && this.hasExplicitPickingStore()) {
        this.syncFromLegacyLocation(cookieLocation);
      }
      lastCookieLocation = cookieLocation;
      this.refreshCurrentLocation();
    }, 1000);

    // Listen for custom events that might be dispatched by delivery-drawer-functions.js
    document.addEventListener('deliveryLocationChanged', (e) => {
      console.log('Delivery location change event detected:', e.detail);
      if (e.detail?.locationId || e.detail?.storeId) {
        const newLocation = e.detail.locationId || e.detail.storeId;
        this.syncFromLegacyLocation(newLocation);
      }
    });

    console.log('Legacy location listeners setup complete');
  }

  /**
   * Helper to get location from cookie
   */
  getCookieLocation() {
    const match = document.cookie.match(/(?:^|;\s*)picking_store=([^;]+)/);
    try {
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Sync our system when legacy system changes location
   */
  syncFromLegacyLocation(newLocation) {
    if (!this.hasExplicitPickingStore()) {
      this.refreshCurrentLocation();
      return;
    }
    const location = String(newLocation ?? '').trim();
    if (!/^\d+$/.test(location) || location === this.currentLocation) return;
    
    console.log('Syncing from legacy location change:', newLocation);
    
    const oldLocation = this.currentLocation;
    this.previousLocation = oldLocation;
    this.currentLocation = location;
    
    // Update our localStorage to stay in sync
    localStorage.setItem('selectedLocation', this.currentLocation);
    
    console.log('Location synced from', oldLocation, 'to', this.currentLocation);
    console.log('Location name:', this.locations[this.currentLocation]?.name);
    
    // Clear cache and dispatch event
    this.clearCache();
    this.dispatchLocationChangeEvent();
  }

  /**
   * Dispatch location change event
   */
  dispatchLocationChangeEvent() {
    const event = new CustomEvent('locationChanged', {
      detail: {
        oldLocation: this.previousLocation,
        newLocation: this.currentLocation,
        locationName: this.locations[this.currentLocation]?.name,
        locationId: this.getCurrentLocationId()
      },
      bubbles: true
    });
    
    document.dispatchEvent(event);
    console.log('Dispatched locationChanged event:', event.detail);
  }

  /**
   * Dispatch ready event when LocationManager is initialized
   */
  dispatchReadyEvent() {
    const event = new CustomEvent('locationManagerReady', {
      detail: {
        currentLocation: this.currentLocation,
        locationName: this.locations[this.currentLocation]?.name,
        availableLocations: this.getAvailableLocations()
      },
      bubbles: true
    });
    
    document.dispatchEvent(event);
    console.log('Dispatched locationManagerReady event');
  }

  /**
   * Get collection products using GraphQL Storefront API
   * OPTIMIZED: Reduced variants from 250→10, added image transforms, streamlined metafields
   */
  async getCollectionProducts(collectionHandle, cursor = null) {
    const cacheKey = `collection_${collectionHandle}_${cursor || 'first'}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log('Using cached collection data for:', collectionHandle);
      return cached;
    }

    const query = `
      query getCollectionProducts($handle: String!, $first: Int!, $after: String) {
        collectionByHandle(handle: $handle) {
          id
          title
          description
          products(first: $first, after: $after) {
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
            edges {
              cursor
              node {
                id
                title
                handle
                vendor
                productType
                tags
                featuredImage {
                  url(transform: { maxWidth: 600, maxHeight: 600, crop: CENTER })
                  altText
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      availableForSale
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
                metafields(identifiers: [
                  {namespace: "custom", key: "uom"},
                  {namespace: "custom", key: "conversion"},
                  {namespace: "custom", key: "unit_price_location_${this.currentLocation}"},
                  {namespace: "custom", key: "price_location_${this.currentLocation}"},
                  {namespace: "custom", key: "special_locations"},
                  {namespace: "custom", key: "special_text_location_${this.currentLocation}"}
                ]) {
                  key
                  value
                  namespace
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      handle: collectionHandle,
      first: this.pageSize,
      after: cursor
    };

    try {
      const result = await this.executeStorefrontQuery(query, variables);
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error fetching collection products:', error);
      throw error;
    }
  }

  /**
   * Get single product using GraphQL Storefront API
   * OPTIMIZED: Reduced variants from 250→100, added image transforms, included media for gallery
   */
  async getProduct(productHandle) {
    const cacheKey = `product_${productHandle}_${this.currentLocation}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log('Using cached product data for:', productHandle);
      return cached;
    }

    const query = `
      query getProduct($handle: String!) {
        productByHandle(handle: $handle) {
          id
          title
          handle
          description
          vendor
          productType
          tags
          featuredImage {
            url(transform: { maxWidth: 800, maxHeight: 800, crop: CENTER })
            altText
          }
          media(first: 10) {
            edges {
              node {
                ... on MediaImage {
                  image {
                    url(transform: { maxWidth: 800, maxHeight: 800, crop: CENTER })
                    altText
                  }
                }
              }
            }
          }
          variants(first: 100) {
            edges {
              node {
                id
                title
                sku
                barcode
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                quantityAvailable
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          metafields(identifiers: [
            {namespace: "local", key: "uom"},
            {namespace: "local", key: "conversion"},
            {namespace: "local", key: "overridecup"},
            {namespace: "local", key: "hideInState"},
            {namespace: "custom", key: "available_locations"},
            {namespace: "custom", key: "special_locations"},
            {namespace: "custom", key: "unit_price_location_${this.currentLocation}"},
            {namespace: "custom", key: "price_location_${this.currentLocation}"},
            {namespace: "custom", key: "special_text_location_${this.currentLocation}"},
            {namespace: "custom", key: "additional_location_${this.currentLocation}"}
          ]) {
            key
            value
            namespace
          }
        }
      }
    `;

    const variables = {
      handle: productHandle
    };

    try {
      const result = await this.executeStorefrontQuery(query, variables);
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  /**
   * Search products using GraphQL Storefront API
   * OPTIMIZED: Reduced variants from 250→10, added image transforms, streamlined metafields
   */
  async searchProducts(searchQuery, cursor = null) {
    const cacheKey = `search_${searchQuery}_${this.currentLocation}_${cursor || 'first'}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log('Using cached search data for:', searchQuery);
      return cached;
    }

    const query = `
      query searchProducts($query: String!, $first: Int!, $after: String) {
        search(query: $query, first: $first, after: $after, types: PRODUCT) {
          edges {
            cursor
            node {
              ... on Product {
                id
                title
                handle
                vendor
                productType
                tags
                featuredImage {
                  url(transform: { maxWidth: 600, maxHeight: 600, crop: CENTER })
                  altText
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      availableForSale
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
                metafields(identifiers: [
                  {namespace: "custom", key: "uom"},
                  {namespace: "custom", key: "conversion"},
                  {namespace: "custom", key: "unit_price_location_${this.currentLocation}"},
                  {namespace: "custom", key: "price_location_${this.currentLocation}"},
                  {namespace: "custom", key: "special_locations"},
                  {namespace: "custom", key: "special_text_location_${this.currentLocation}"}
                ]) {
                  key
                  value
                  namespace
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    `;

    const variables = {
      query: searchQuery,
      first: this.pageSize,
      after: cursor
    };

    try {
      const result = await this.executeStorefrontQuery(query, variables);
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /**
   * Execute GraphQL query against Shopify Storefront API
   */
  async executeStorefrontQuery(query, variables = {}) {
    if (!this.storefrontToken) {
      throw new Error('Storefront access token not found');
    }

    const endpoint = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify({
        query,
        variables
      })
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
    }

    return data.data;
  }

  /**
   * Filter products to only show those available at current location
   */
  filterAvailableProducts(products) {
    if (!this.hasExplicitPickingStore()) return products;
    return products.filter(product => {
      // Check if product has location-specific pricing
      const locationPriceKey = `price_location_${this.currentLocation}`;
      const hasLocationPrice = product.metafields?.some(m => m.key === locationPriceKey && m.value);
      
      if (hasLocationPrice) return true;
      
      // Check if any variant has the location in its title (legacy system)
      return product.variants?.edges?.some(({ node: variant }) => {
        return variant.title && variant.title.toLowerCase().includes(`s:${this.currentLocation}`);
      });
    });
  }

  /**
   * Get product inventory at current location
   */
  getProductInventoryAtLocation(product) {
    const locationId = this.getCurrentLocationId();
    if (!locationId) return { available: false, quantity: 0 };

    // This would require additional GraphQL queries to get inventory levels
    // For now, we'll use the general availableForSale flag
    const firstVariant = product.variants?.edges?.[0]?.node;
    return {
      available: firstVariant?.availableForSale || false,
      quantity: firstVariant?.quantityAvailable || 0
    };
  }

  /**
   * Refresh all product displays when location changes
   */
  refreshProductDisplays() {
    console.log('Refreshing product displays for location:', this.currentLocation);
    
    // Clear cache to force fresh data
    this.clearCache();
    
    // Trigger refresh events for any listening components
    document.dispatchEvent(new CustomEvent('refreshProducts', {
      detail: { location: this.currentLocation },
      bubbles: true
    }));
  }

  /**
   * Get list of available locations
   */
  getAvailableLocations() {
    return Object.entries(this.locations).map(([id, location]) => ({
      id,
      name: location.name,
      shopifyId: location.id,
      isDefault: location.isDefault || false
    }));
  }

  /**
   * Create a test location selector (for development)
   */
  createTestSelector() {
    if (!DEV_MODE) return;
    
    const selector = document.createElement('select');
    selector.id = 'location-test-selector';
    selector.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 9999;
      padding: 8px;
      background: white;
      border: 2px solid #333;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
    `;
    
    // Add options
    Object.entries(this.locations).forEach(([id, location]) => {
      const option = document.createElement('option');
      option.value = id;
      option.textContent = `${id} - ${location.name}`;
      option.selected = id === this.currentLocation;
      selector.appendChild(option);
    });
    
    // Add change listener
    selector.addEventListener('change', (e) => {
      console.log('Test selector: changing location to', e.target.value);
      this.setLocation(e.target.value);
    });
    
    // Add to page
    document.body.appendChild(selector);
    
    console.log('Test location selector added (DEV_MODE)');
  }

  /**
   * Batch product processing for better performance
   * OPTIMIZED: Reduced variants from 250→10, added image transforms, streamlined metafields
   * Note: Using nodes query for batch fetching (requires product GIDs not handles)
   */
  async getBatchProducts(productHandles) {
    if (!Array.isArray(productHandles) || productHandles.length === 0) {
      return [];
    }

    // Split into chunks of reasonable size
    const chunkSize = 10;
    const chunks = [];
    for (let i = 0; i < productHandles.length; i += chunkSize) {
      chunks.push(productHandles.slice(i, i + chunkSize));
    }

    const results = [];
    
    for (const chunk of chunks) {
      const query = `
        query getBatchProducts($handles: [ID!]!) {
          nodes(ids: $handles) {
            ... on Product {
              id
              title
              handle
              vendor
              productType
              tags
              featuredImage {
                url(transform: { maxWidth: 600, maxHeight: 600, crop: CENTER })
                altText
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    availableForSale
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
              metafields(identifiers: [
                {namespace: "custom", key: "uom"},
                {namespace: "custom", key: "conversion"},
                {namespace: "custom", key: "available_locations"},
                {namespace: "custom", key: "unit_price_location_${this.currentLocation}"},
                {namespace: "custom", key: "price_location_${this.currentLocation}"},
                {namespace: "custom", key: "special_locations"},
                {namespace: "custom", key: "special_text_location_${this.currentLocation}"},
                {namespace: "custom", key: "additional_location_${this.currentLocation}"}
              ]) {
                key
                value
                namespace
              }
            }
          }
        }
      `;

      // Convert handles to GraphQL IDs (handles can be numeric IDs or string handles)
      const graphqlIds = chunk.map(handle => {
        // If it's already a GID, use it as-is
        if (typeof handle === 'string' && handle.startsWith('gid://')) {
          return handle;
        }
        // Otherwise, convert to Product GID
        return `gid://shopify/Product/${handle}`;
      });
      
      try {
        const result = await this.executeStorefrontQuery(query, { handles: graphqlIds });
        if (result.nodes) {
          results.push(...result.nodes.filter(node => node !== null));
        }
      } catch (error) {
        console.error('Error in batch product fetch:', error);
      }
    }

    return results;
  }

  /**
   * Cache management
   */
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > this.cacheExpiry) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
    console.log('Cache cleared');
  }

  /**
   * Get product with caching
   */
  async getProductWithCache(productHandle) {
    const cacheKey = `product_${productHandle}_${this.currentLocation}`;
    let product = this.getFromCache(cacheKey);
    
    if (!product) {
      const result = await this.getProduct(productHandle);
      product = result.productByHandle;
      this.setCache(cacheKey, product);
    }
    
    return product;
  }
}

// Initialize LocationManager when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (!window.locationManager) {
    window.locationManager = new LocationManager();
    console.log('LocationManager initialized and available globally');
    
    // Create test selector in dev mode
    if (DEV_MODE) {
      window.locationManager.createTestSelector();
    }
  }
});

// Global helper so cart.js / store-picker.js / product-card-data.js can check
// "has the shopper explicitly chosen a picking store" cheaply, even before
// window.locationManager has initialized (this script tag loads before
// location-manager instantiates on DOMContentLoaded, and cart.js runs even
// earlier in the deferred script order - see layout/theme.liquid).
window.hasPickingStore = function () {
  return getExplicitPickingStoreFromStorage() !== null;
};

// ===== GLOBAL COLLECTION LINK INTERCEPTOR =====
// Ensures all collection links include the location filter for server-side filtering.
// All unified specials collections (online-specials, grocery-specials, and the other
// category specials) use special_locations. Legacy per-store *-XX URLs bounce to
// their unified handle (see isLegacyOnlineSpecialsStorePath / isLegacyGrocerySpecialsStorePath).
(function() {
  const AVAILABLE_FILTER_KEY = 'filter.p.m.custom.available_locations';
  const SPECIAL_FILTER_KEY = 'filter.p.m.custom.special_locations';
  // Unified specials collections — includes online-specials and grocery-specials.
  const SPECIALS_HANDLES = new Set([
    'online-specials',
    'grocery-specials',
    'fruit-and-veg-specials',
    'butcher-specials',
    'seafood-specials',
    'organic-specials',
    'fridge-specials',
    'snacks-specials',
    'drinks-specials',
    'pet-specials',
    'liquor-specials',
    'fruit-veg-box-specials',
    'meal-kit-specials',
    'fruit-specials',
    'vegies-specials',
    'cheese-specials',
    'bakery-specials'
  ]);

  function getActiveLocation() {
    return getExplicitPickingStoreFromStorage();
  }

  function hasExplicitLocation() {
    return typeof window.hasPickingStore === 'function' && window.hasPickingStore();
  }
  
  function isCollectionLink(href) {
    return href && href.includes('/collections/') && !href.includes('/products/');
  }

  function getCollectionHandle(pathname) {
    const match = (pathname || '').match(/^\/collections\/([^\/]+)\/?$/);
    return match ? match[1] : '';
  }

  function isLegacyOnlineSpecialsStorePath(pathname) {
    return /^\/collections\/online-specials-\d+\/?$/.test(pathname || '');
  }

  // grocery-specials was previously split into per-store smart collections
  // (grocery-specials-<store>); it is now unified like online-specials, so legacy
  // links to those per-store URLs bounce back to /collections/grocery-specials.
  function isLegacyGrocerySpecialsStorePath(pathname) {
    return /^\/collections\/grocery-specials-\d+\/?$/.test(pathname || '');
  }

  function isCategorySpecialsPath(pathname) {
    return SPECIALS_HANDLES.has(getCollectionHandle(pathname));
  }

  function applyLocationFilters(url, currentLocation) {
    // Bounce legacy per-store specials URLs back to their unified handle.
    if (isLegacyOnlineSpecialsStorePath(url.pathname)) {
      url.pathname = '/collections/online-specials';
    } else if (isLegacyGrocerySpecialsStorePath(url.pathname)) {
      url.pathname = '/collections/grocery-specials';
    }

    // With no selection, strip old store filters so browsing stays a union catalogue.
    if (!hasExplicitLocation()) {
      url.searchParams.delete(AVAILABLE_FILTER_KEY);
      url.searchParams.delete(SPECIAL_FILTER_KEY);
      return;
    }

    if (url.searchParams.get(AVAILABLE_FILTER_KEY) !== currentLocation) {
      url.searchParams.set(AVAILABLE_FILTER_KEY, currentLocation);
    }

    if (isCategorySpecialsPath(url.pathname)) {
      if (url.searchParams.get(SPECIAL_FILTER_KEY) !== currentLocation) {
        url.searchParams.set(SPECIAL_FILTER_KEY, currentLocation);
      }
    }
  }

  function addLocationToUrl(href) {
    try {
      const url = new URL(href, window.location.origin);
      applyLocationFilters(url, getActiveLocation());
      return url.toString();
    } catch(e) {
      return href;
    }
  }

  // Normalizes legacy per-store specials URLs (online-specials-XX, grocery-specials-XX)
  // to their unified handle and ensures available_locations + special_locations are set.
  (function ensureSpecialsFiltersOnLoad() {
    try {
      const url = new URL(window.location.href);
      if (
        !isCategorySpecialsPath(url.pathname) &&
        !isLegacyOnlineSpecialsStorePath(url.pathname) &&
        !isLegacyGrocerySpecialsStorePath(url.pathname)
      ) return;

      const before = url.toString();
      applyLocationFilters(url, getActiveLocation());
      if (url.toString() !== before) {
        window.location.replace(url.toString());
      }
    } catch (e) {
      // Never block page load over the redirect.
    }
  })();
  
  // Intercept all clicks on collection links
  document.addEventListener('click', function(e) {
    const link = e.target.closest('a');
    if (!link) return;
    
    const href = link.getAttribute('href');
    if (!isCollectionLink(href)) return;
    
    // Don't intercept if modifier keys are pressed (new tab, etc.)
    if (e.ctrlKey || e.metaKey || e.shiftKey) return;
    
    // Add location filter to URL
    const newHref = addLocationToUrl(href);
    
    if (newHref !== href) {
      e.preventDefault();
      
      // Use form submission to ensure Shopify processes the filter
      // This is more reliable than window.location.href for S&D filters
      const url = new URL(newHref, window.location.origin);
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = url.pathname;
      form.style.display = 'none';
      
      // Add all URL params as hidden inputs
      url.searchParams.forEach(function(value, key) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      
      document.body.appendChild(form);
      form.submit();
    }
  }, true); // Use capture phase to intercept early
  
  console.log('📍 Collection link interceptor active - all collection links will include location filter');
})();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LocationManager;
} 